import { useEffect, useState, type ReactNode } from 'react';
import Backdrop from './components/Backdrop';
import Hero from './components/Hero';
import NotifyForm from './components/NotifyForm';
import { NOTIFY_SWITCH, createAnalytics, nullAnalytics, type Analytics } from './analytics/client';
import { DEFAULT_COPY } from '../core/copy';
import {
  type AnalyticsEvent,
  landingPageViewed,
  loginOpened,
  toolOpened,
  notifyFormSubmitted,
  notifySignupConfirmed,
  notifySignupFailed,
  notifySignupRejected,
} from '../core/events';

export { DEFAULT_COPY };

export interface AppProps {
  /** Injected in tests. In the browser the real adapter is built on mount. */
  analytics?: Analytics;
  /** Clerk supplies this render function in production; tests can keep auth out of scope. */
  renderAccountAction?: (onLogin: () => void) => ReactNode;
  showTools?: boolean;
}

export default function App({ analytics: injected, renderAccountAction, showTools = false }: AppProps = {}) {
  const [objectivesVisible, setObjectivesVisible] = useState(false);
  const [analytics, setAnalytics] = useState<Analytics | null>(injected ?? null);

  /**
   * Every track call goes through here. The adapter already swallows vendor errors, but
   * this is the boundary that matters: a throw from an effect kills the mount, and a
   * throw from the submit handler loses the signup. An analytics vendor does not get to
   * do either.
   */
  const track = (event: AnalyticsEvent) => {
    try {
      analytics?.track(event);
    } catch (error) {
      console.warn('[analytics] track failed', error);
    }
  };

  useEffect(() => {
    if (injected) return;

    let live = true;
    createAnalytics()
      .then((a) => live && setAnalytics(a))
      .catch(() => live && setAnalytics(nullAnalytics()));

    return () => {
      live = false;
    };
  }, [injected]);

  useEffect(() => {
    if (analytics) track(landingPageViewed(DEFAULT_COPY.variant));
    // `track` closes over `analytics`; re-running on anything else would double-count.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics]);

  useEffect(() => {
    setObjectivesVisible(false);
    return analytics?.watchObjectivesVisibility?.(setObjectivesVisible);
  }, [analytics]);

  // While the adapter is still being constructed, render the form disabled rather than
  // showing the switch-off fallback and swapping it a moment later. A layout jump on the
  // only CTA is worse than a briefly inert input.
  const resolving = analytics === null;
  const gateOpen = resolving || analytics.gate(NOTIFY_SWITCH);
  const copy = DEFAULT_COPY;
  // A stable label on every funnel event saying which page sent it, not an arm: the copy
  // experiment was retired with Statsig and this page has only ever shipped one wording.
  const variant = () => DEFAULT_COPY.variant;

  return (
    <>
      <Backdrop />
      <main className="page">
        <div className="card">
          <Hero
            copy={copy}
            accountAction={renderAccountAction?.(() => track(loginOpened(variant())))}
            showTools={showTools}
            objectivesVisible={objectivesVisible}
            onToolOpen={() => track(toolOpened('objectives'))}
          />

          {!showTools && (gateOpen ? (
            <NotifyForm
              disabled={resolving}
              onSubmitAttempt={() => track(notifyFormSubmitted(variant()))}
              onConfirmed={() => track(notifySignupConfirmed(variant()))}
              onRejected={(reason) => track(notifySignupRejected(reason))}
              onFailed={(status) => track(notifySignupFailed(status))}
            />
          ) : (
            <p className="fallback">More soon. We are not collecting emails right now.</p>
          ))}
        </div>
      </main>
    </>
  );
}
